import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";

/**
 * Página para establecer una nueva contraseña tras el flujo de recuperación.
 * Supabase incluye el access_token/refresh_token en el hash de la URL.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (type !== "recovery" || !accessToken || !refreshToken) {
      setError("El enlace de recuperación no es válido o ha expirado.");
      return;
    }

    supabase.auth
      .setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(({ error: sessionError }) => {
        if (sessionError) {
          setError(sessionError.message);
          return;
        }
        setReady(true);
      });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      showToast("Contraseña actualizada correctamente.", "success");
      navigate("/login", { replace: true });
    } catch {
      setError("Ocurrió un error inesperado. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-store-2-line text-xl text-white"></i>
            </span>
            <span className="font-heading text-xl font-bold text-foreground-950">
              Vende<span className="text-primary-500">Ya</span>
            </span>
          </Link>
        </div>

        <div className="mb-6 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            Nueva contraseña
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            Escribe tu nueva contraseña para continuar.
          </p>
        </div>

        {error && !ready ? (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-accent-200 bg-accent-100 p-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-50">
              <i className="ri-error-warning-line text-2xl text-accent-600"></i>
            </span>
            <p className="text-sm text-accent-900">{error}</p>
            <Link
              to="/forgot-password"
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Solicitar nuevo enlace
            </Link>
          </div>
        ) : !ready ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="text-sm text-foreground-600">Verificando tu enlace…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-100 px-3 py-2.5 text-sm text-accent-900"
              >
                <i className="ri-error-warning-fill mt-0.5 text-accent-600"></i>
                <span className="leading-snug">{error}</span>
              </div>
            )}

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium text-foreground-700"
              >
                Nueva contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                  <i className="ri-lock-line text-base"></i>
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-11 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                >
                  <i
                    className={`text-base ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
                  ></i>
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirm"
                className="mb-1.5 block text-sm font-medium text-foreground-700"
              >
                Confirmar contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                  <i className="ri-lock-line text-base"></i>
                </span>
                <input
                  id="confirm"
                  name="confirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repite tu contraseña"
                  className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting && (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              )}
              {submitting ? "Guardando..." : "Guardar contraseña"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}